"use client";

import React, { useState, useEffect } from "react";
import { X, Scale, Gavel, Calendar, CheckCircle2, ArrowRight } from "lucide-react";
import { LegalCase } from "@/lib/demos/legalData";

interface LegalStageModalProps {
  isOpen: boolean;
  onClose: () => void;
  caseItem: LegalCase | null;
  targetStage?: LegalCase["stage"] | null;
  onUpdateCase: (updated: LegalCase) => void;
}

const STAGES: Array<LegalCase["stage"]> = [
  "New Case Intake",
  "Consultation & Retainer",
  "Drafting & Evidence",
  "Filed in Court / Listed",
  "Hearing & Arguments",
  "Decree & Closed (Won)",
];

export default function LegalStageModal({
  isOpen,
  onClose,
  caseItem,
  targetStage,
  onUpdateCase,
}: LegalStageModalProps) {
  const [stage, setStage] = useState<LegalCase["stage"]>("Hearing & Arguments");
  const [nextHearingDate, setNextHearingDate] = useState("");
  const [itemNumber, setItemNumber] = useState<number>(12);
  const [courtRoom, setCourtRoom] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (caseItem) {
      setStage(targetStage || caseItem.stage);
      setNextHearingDate(caseItem.next_hearing_date || new Date(Date.now() + 14 * 86400000).toISOString().split("T")[0]);
      setItemNumber(caseItem.item_number || 10);
      setCourtRoom(caseItem.court_room || "Court Room 14");
      setNotes(caseItem.notes || "");
    }
  }, [caseItem, targetStage, isOpen]);

  if (!isOpen || !caseItem) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const newAppearance = {
        date: new Date().toISOString().split("T")[0],
        court: caseItem.court_name,
        courtRoom,
        judgeBench: caseItem.judge_bench,
        itemNumber,
        purpose: "Arguments" as const,
        orderSummary: notes || `Matter advanced to ${stage}.`,
      };

      const updatedAppearances = [newAppearance, ...(caseItem.appearances || [])];

      const res = await fetch(`/api/demos/legal/cases/${caseItem.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          stage,
          next_hearing_date: nextHearingDate,
          item_number: itemNumber,
          court_room: courtRoom,
          notes,
          appearances: updatedAppearances,
        }),
      });

      if (!res.ok) throw new Error("Failed to advance matter stage");
      const data = await res.json();
      if (data.case) {
        onUpdateCase(data.case);
        onClose();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-md overflow-y-auto animate-fade-in">
      <div className="relative w-full max-w-lg p-5 sm:p-6 rounded-2xl bg-slate-900 border border-slate-800 shadow-2xl text-slate-100 my-8">
        
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="flex items-center gap-2.5 pb-3 border-b border-slate-800">
          <div className="p-2 rounded-xl bg-amber-500/10 text-amber-400">
            <Gavel className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-serif font-bold text-white">
              Advance Matter Stage & Record Order Sheet
            </h3>
            <p className="text-[11px] text-slate-400 font-mono">
              {caseItem.case_number} • {caseItem.title}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-4 space-y-3.5 text-xs">
          {/* Target Stage */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-300 mb-1">
              Pipeline Stage
            </label>
            <select
              value={stage}
              onChange={(e) => setStage(e.target.value as any)}
              className="w-full px-3 py-2 bg-slate-850 border border-slate-750 rounded-xl text-xs text-white"
            >
              {STAGES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {/* Next Hearing Date */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                Next Hearing Date (NDOH)
              </label>
              <input
                type="date"
                value={nextHearingDate}
                onChange={(e) => setNextHearingDate(e.target.value)}
                className="w-full px-3 py-2 bg-slate-850 border border-slate-750 rounded-xl text-xs text-white font-mono"
                required
              />
            </div>

            {/* Board Item Number */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-300 mb-1">
                Item # on Cause List
              </label>
              <input
                type="number"
                value={itemNumber}
                onChange={(e) => setItemNumber(Number(e.target.value))}
                className="w-full px-3 py-2 bg-slate-850 border border-slate-750 rounded-xl text-xs text-white font-mono"
              />
            </div>
          </div>

          {/* Court Room */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-300 mb-1">
              Court Room / Chamber
            </label>
            <input
              type="text"
              value={courtRoom}
              onChange={(e) => setCourtRoom(e.target.value)}
              placeholder="e.g. Court Room 14 (Commercial Bench)"
              className="w-full px-3 py-2 bg-slate-850 border border-slate-750 rounded-xl text-xs text-white"
            />
          </div>

          {/* Order Summary */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-300 mb-1">
              Daily Order Summary / Judge Directions
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="e.g. Notice issued. Interim stay granted till next date. Respondent directed to file counter-affidavit in 2 weeks."
              className="w-full px-3 py-2 bg-slate-850 border border-slate-750 rounded-xl text-xs text-white resize-none"
            />
          </div>

          {/* Submit */}
          <div className="pt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-750 text-slate-300 text-xs font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs shadow-lg transition-all flex items-center gap-1.5"
            >
              {loading ? "Recording..." : "Update Matter & Order Sheet"}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}
